/**
 * Page Frame (H-2026-09-12-130) — one shell for all five modes.
 *
 * Every mode = PageHeader (title + subtitle + actions slot) + PageBody (the
 * existing per-mode content, untouched). The header lives ONCE in the shared
 * .stage-header; applyPageFrame() updates it in place on every mode switch
 * (same DOM nodes, textContent swap only) so the title area never jumps.
 *
 * The module also ships the two shared states the modes used to hand-roll
 * (three+ placeholder styles before this card):
 *   EmptyState       — icon + one line + optional primary action
 *   LoadingSkeleton  — shimmer blocks (the H-129 fs-shimmer technique);
 *                      spinners are banned in page chrome.
 */

import { esc, escAttr } from '../../utils/escape.js'

// Mode copy contract (task card §2/§3): titles are page chrome (English);
// subtitles keep the per-mode Chinese line exactly as the card specifies.
export const PAGE_FRAMES = {
  create: { title: 'Create', subtitle: 'One image · Four styles · On the product', icon: 'ph-magic-wand' },
  design: { title: 'Design', subtitle: 'Material × Blank = Product', icon: 'ph-paint-brush' },
  gallery: { title: 'Gallery', subtitle: 'Your material library', icon: 'ph-images-square' },
  products: { title: 'Products', subtitle: 'Blank product catalog', icon: 'ph-t-shirt' },
  me: { title: 'Me', subtitle: 'Your account & works', icon: 'ph-user-circle' },
}

const FALLBACK_FRAME = { title: 'Create', subtitle: '', icon: 'ph-square' }

/**
 * Pure: frame config for a mode. Unknown modes fall back to the create frame
 * so a bad ?mode= never blanks the header.
 */
export function resolvePageFrame(mode) {
  return PAGE_FRAMES[mode] || FALLBACK_FRAME
}

/**
 * Pure: full frame markup — PageHeader (title + subtitle + actions slot) +
 * PageBody. The app's live header is static in index.html and updated via
 * applyPageFrame(); this generator is the canonical shape for tests and for
 * any host that needs to mount a frame from scratch.
 */
export function renderPageFrame({ title, subtitle, actions = '', body = '' } = {}) {
  return `<div class="stage-heading">
    <span class="stage-title" id="stageTitle">${esc(title)}</span>
    <span class="stage-subtitle" id="stageSubtitle">${esc(subtitle || '')}</span>
  </div>
  <div class="stage-actions" id="stageActions">${actions}</div>
  <div class="pf-body" data-page-body>${body}</div>`
}

/**
 * Ensure the shared .stage-header carries the subtitle element (the title
 * span is static in index.html). Idempotent safety net for embed contexts
 * and test hosts that mount a bare header.
 */
export function ensurePageFrame(root = document) {
  const header = root.querySelector('.stage-header')
  if (!header) return null
  if (!header.querySelector('#stageSubtitle')) {
    const titleEl = header.querySelector('#stageTitle')
    const heading = root.createElement('div')
    heading.className = 'stage-heading'
    const subtitle = root.createElement('span')
    subtitle.className = 'stage-subtitle'
    subtitle.id = 'stageSubtitle'
    if (titleEl) {
      titleEl.replaceWith(heading)
      heading.append(titleEl, subtitle)
    } else {
      header.prepend(heading)
      heading.append(subtitle)
    }
  }
  return header
}

/**
 * Update the shared PageHeader for `mode` IN PLACE — same nodes, no header
 * re-mount, so switching pages never jumps the title area. Also scopes the
 * actions slot: a [data-stage-mode] group shows only on its own mode (the
 * value 'all' keeps a control visible on every mode).
 */
export function applyPageFrame(mode, root = document) {
  const header = ensurePageFrame(root)
  if (!header) return null
  const frame = resolvePageFrame(mode)
  const titleEl = header.querySelector('#stageTitle')
  const subtitleEl = header.querySelector('#stageSubtitle')
  if (titleEl) titleEl.textContent = frame.title
  if (subtitleEl) subtitleEl.textContent = frame.subtitle
  header.dataset.pageMode = mode
  header.querySelectorAll('[data-stage-mode]').forEach((el) => {
    el.hidden = el.dataset.stageMode !== mode && el.dataset.stageMode !== 'all'
  })
  return header
}

/**
 * Pure: unified empty state — icon + one line + optional hint + optional
 * primary action. Replaces the per-mode placeholder styles (mode-placeholder
 * / placeholder-text / me-empty / ws-gallery__empty).
 */
export function renderEmptyState({
  icon = 'ph-image',
  message = '',
  hint = '',
  actionLabel = '',
  actionId = '',
} = {}) {
  const cta = actionLabel
    ? `<button type="button" class="pf-empty__cta"${actionId ? ` id="${escAttr(actionId)}"` : ''}>${esc(actionLabel)}</button>`
    : ''
  return `<div class="pf-empty">
    <i class="ph ${escAttr(icon)}" aria-hidden="true"></i>
    <p class="pf-empty__msg">${esc(message)}</p>
    ${hint ? `<small class="pf-empty__hint">${esc(hint)}</small>` : ''}
    ${cta}
  </div>`
}

/**
 * Pure: unified loading state — shimmer skeleton blocks (H-129 fs-shimmer
 * gradient), NEVER a spinner.
 */
export function renderLoadingSkeleton({ count = 3 } = {}) {
  const blocks = '<div class="pf-skel" aria-hidden="true"></div>'.repeat(Math.max(1, count))
  return `<div class="pf-skel-list" role="status" aria-label="Loading">${blocks}</div>`
}

/**
 * Migrate an existing control into the header actions slot for `mode`
 * (task card §2: design's Share & Buy moves up; the node itself moves so its
 * listeners and id survive). Returns a restore() that puts the node back
 * exactly where it was and clears the mode scoping.
 */
export function pinToPageActions(el, mode, root = document) {
  const actions = root.getElementById('stageActions')
  if (!el || !actions || el.parentNode === actions) return () => {}
  const home = el.parentNode
  const homeNext = el.nextSibling
  el.dataset.stageMode = mode
  el.hidden = false
  actions.appendChild(el)
  return () => {
    el.removeAttribute('data-stage-mode')
    el.hidden = false
    if (home) home.insertBefore(el, homeNext)
  }
}

/* ---- L2 subtab bar (H-2026-09-12-135a) ------------------------------
 * The horizontal tab strip under the PageHeader. State/URL/registration live
 * in core/subtabs.js; page-frame owns ONLY the markup. Same visual language
 * as the H-130 header: quiet text, 2px underline for the active tab. */

/**
 * Pure: subtab bar markup — tab list + an aside slot (modes park small
 * contextual readouts there, e.g. the gallery image count). Buttons carry
 * data-subtab; core/subtabs.js owns the click delegation.
 */
export function renderSubtabBar(tabs = [], activeId = null) {
  const buttons = tabs
    .map((t) => {
      const on = t.id === activeId
      return `<button type="button" class="pf-subtab${on ? ' active' : ''}" role="tab" data-subtab="${escAttr(t.id)}" aria-selected="${on ? 'true' : 'false'}">${esc(t.label)}</button>`
    })
    .join('')
  return `<div class="pf-subtabs__list" role="tablist">${buttons}</div>
  <div class="pf-subtabs__aside" id="stageSubtabsAside"></div>`
}

/**
 * Update the shared #stageSubtabs bar for `mode` IN PLACE. The bar node
 * itself never re-mounts; buttons rebuild only when the tab-id set changes
 * (signature diff), otherwise just the active class flips — the strip never
 * jumps on a same-mode tab switch. Modes with <2 tabs get NO bar (task
 * card: single-tab modes hide the strip). Returns the bar element or null
 * when the host is absent (embed/test contexts).
 */
export function applySubtabBar(mode, { tabs = [], activeId = null } = {}, root = document) {
  const bar = root.querySelector('#stageSubtabs')
  if (!bar) return null
  if (!tabs || tabs.length < 2) {
    bar.hidden = true
    bar.innerHTML = ''
    delete bar.dataset.subtabSig
    return bar
  }
  bar.hidden = false
  const sig = tabs.map((t) => t.id).join('|')
  if (bar.dataset.subtabSig !== sig) {
    bar.dataset.subtabSig = sig
    bar.innerHTML = renderSubtabBar(tabs, activeId)
  }
  bar.querySelectorAll('.pf-subtab').forEach((btn) => {
    const on = btn.dataset.subtab === activeId
    btn.classList.toggle('active', on)
    btn.setAttribute('aria-selected', on ? 'true' : 'false')
  })
  return bar
}
